/**
 * Tab 4: Governance
 * Data processing posture, audit capabilities, and upcoming F19/F20 features.
 * Designed for the AI Governance Committee and CFO reporting.
 */

import React from 'react';
import { LoadingPanel, ErrorPanel } from '../shared/LoadingPanel';
import { useFeedback, formatRelativeTime } from '../hooks/useAdminData';
import { getNegativeFeedback } from '../../../api/adminApi';
import type { NegativeFeedbackItem } from '../../../types/admin';

export const Governance: React.FC = () => {
  const feedback = useFeedback(30, 120_000);
  const [negFeedback, setNegFeedback] = React.useState<NegativeFeedbackItem[]>([]);
  const [negLoading, setNegLoading] = React.useState(true);
  // H5: surface load failures instead of silently showing an empty list —
  // an empty state and a fetch failure look identical otherwise.
  const [negError, setNegError] = React.useState<string | null>(null);

  React.useEffect(() => {
    getNegativeFeedback(10)
      .then((result) => {
        // API may return {items: [...]} or a plain array
        if (Array.isArray(result)) {
          setNegFeedback(result);
        } else if (result && Array.isArray((result as Record<string, unknown>).items)) {
          setNegFeedback((result as Record<string, unknown>).items as NegativeFeedbackItem[]);
        } else {
          setNegFeedback([]);
        }
        setNegError(null);
      })
      .catch((err) => {
        console.error('[Governance] Failed to load negative feedback:', err);
        setNegFeedback([]);
        setNegError(err instanceof Error ? err.message : 'Failed to load negative feedback');
      })
      .finally(() => setNegLoading(false));
  }, []);

  return (
    <div className="adm-tab-content">
      <div className="adm-tab-header">
        <h2 className="adm-tab-title">Governance</h2>
        <span className="adm-refresh-label">
          Updated {formatRelativeTime(feedback.lastRefreshed)}
        </span>
      </div>

      {/* Data Processing Posture */}
      <div className="adm-card adm-governance-posture">
        <div className="adm-card-title">Azure OpenAI Data Processing</div>
        <table className="adm-details-table">
          <tbody>
            <tr>
              <td className="adm-details-label">Service type</td>
              <td>Azure OpenAI Service (not consumer ChatGPT)</td>
            </tr>
            <tr>
              <td className="adm-details-label">Data residency</td>
              <td>East US (same tenant)</td>
            </tr>
            <tr>
              <td className="adm-details-label">Training opt-out</td>
              <td>
                <span style={{ color: '#28A745', fontWeight: 600 }}>Enabled</span> (enterprise agreement)
              </td>
            </tr>
            <tr>
              <td className="adm-details-label">Default retention</td>
              <td>30 days (abuse monitoring)</td>
            </tr>
            <tr>
              <td className="adm-details-label">Enterprise opt-out</td>
              <td>Available — contact Azure support</td>
            </tr>
            <tr>
              <td className="adm-details-label">Prompt logging</td>
              <td>
                <span style={{ color: '#28A745', fontWeight: 600 }}>Disabled</span> in production
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Current Audit Capabilities */}
      <div className="adm-card" style={{ marginTop: 16 }}>
        <div className="adm-card-title">Current Audit Capabilities</div>
        {feedback.loading && !feedback.data ? (
          <LoadingPanel message="Loading feedback data..." />
        ) : feedback.error && !feedback.data ? (
          <ErrorPanel message={feedback.error} onRetry={feedback.refresh} />
        ) : feedback.data ? (
          <div className="adm-audit-grid" style={{ marginTop: 12 }}>
            <div className="adm-audit-stat">
              <span className="adm-audit-value adm-mono" style={{ color: '#28A745' }}>
                {feedback.data.positive}
              </span>
              <span className="adm-audit-label">Thumbs Up</span>
            </div>
            <div className="adm-audit-stat">
              <span className="adm-audit-value adm-mono" style={{ color: '#DC3545' }}>
                {feedback.data.negative}
              </span>
              <span className="adm-audit-label">Thumbs Down</span>
            </div>
            <div className="adm-audit-stat">
              <span className="adm-audit-value adm-mono">
                {feedback.data.total_feedback}
              </span>
              <span className="adm-audit-label">Total Feedback</span>
            </div>
            <div className="adm-audit-stat">
              <span
                className="adm-audit-value adm-mono"
                style={{
                  color:
                    feedback.data.satisfaction_rate == null
                      ? '#495057'
                      : feedback.data.satisfaction_rate >= 80
                        ? '#28A745'
                        : feedback.data.satisfaction_rate >= 60
                          ? '#FFC107'
                          : '#DC3545',
                }}
              >
                {feedback.data.satisfaction_rate != null ? `${feedback.data.satisfaction_rate.toFixed(1)}%` : '—'}
              </span>
              <span className="adm-audit-label">Satisfaction Rate</span>
            </div>
          </div>
        ) : null}

        {/* Negative feedback load error — surfaced instead of silently
            showing an empty list (H5). */}
        {!negLoading && negError && (
          <div role="alert" style={{ marginTop: 16, padding: 12, background: '#F8D7DA', color: '#721C24', borderRadius: 4, fontSize: '0.875rem' }}>
            Couldn't load recent negative feedback: {negError}
          </div>
        )}

        {/* Negative feedback list */}
        {!negLoading && !negError && negFeedback.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontWeight: 600, fontSize: '0.8rem', marginBottom: 8, color: '#495057' }}>
              Recent Negative Feedback
            </div>
            <table className="adm-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Query</th>
                  <th>Comment</th>
                </tr>
              </thead>
              <tbody>
                {negFeedback.map((nf, i) => {
                  // API may use different field names
                  const raw = nf as unknown as Record<string, unknown>;
                  const ts = (raw.timestamp ?? '') as string;
                  const query = (raw.query ?? raw.response_preview ?? raw.chat_id ?? '') as string;
                  const comment = (raw.comment ?? raw.feedback_comment ?? '') as string;
                  return (
                    <tr key={i}>
                      <td className="adm-mono" style={{ fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
                        {ts ? new Date(ts).toLocaleDateString() : '—'}
                      </td>
                      <td style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {query || '—'}
                      </td>
                      <td className="adm-muted">{comment || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* F20: Session Document Uploads */}
      <div className="adm-card adm-coming-soon" style={{ marginTop: 16 }}>
        <div className="adm-card-title">
          <span className="adm-badge-info" style={{ marginRight: 8 }}>F20</span>
          Session Document Uploads — Coming v4.0
        </div>
        <p className="adm-muted" style={{ margin: '8px 0 0', lineHeight: 1.6, fontSize: '0.8rem' }}>
          Session-scoped document uploads (F20) are planned for v4.0.
          When active, this panel will show per-user upload volume, document TTL status
          (90-day inactivity expiry), and an attachment audit log.
          Governance posture: uploads scoped to uploader's chat only.
          Never visible to other users. Auditable. Auto-expiring.
        </p>
      </div>
    </div>
  );
};
